import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const BENEFITS = [
  { num: '01', label: '24/7 Fleet Access',      detail: 'Any truck, any time. No scheduling, no waiting around.' },
  { num: '02', label: 'Free Maintenance',         detail: 'Cheaper or fully covered repairs on all company vehicles.' },
  { num: '03', label: 'Driver Training',          detail: 'Structured programs from your first shift to senior operator.' },
  { num: '04', label: 'Company Events',           detail: 'Regular runs, meets, and community activities throughout the year.' },
  { num: '05', label: 'Active Community',         detail: 'A tight-knit crew that actually looks out for each other on the road.' },
  { num: '06', label: 'Career Progression',       detail: 'Clear paths upward — from driver to senior operator to management.' },
];

const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-0 bg-[#0a0a0c]">

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="px-6 md:px-12 max-w-7xl mx-auto mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          data-testid="about-section"
        >
          <span className="overline">About Us</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mt-4 mb-14 tracking-tighter uppercase leading-[0.92]">
            Go Truck<br />Yourself
          </h1>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Text */}
            <div className="space-y-6">
              <div className="border-l-4 border-purple-600 pl-6">
                <p className="text-lg text-[#a1a1aa] leading-relaxed">
                  GTY is a premier trucking company located next to YouTools, built on reliability, community, and professionalism. We're more than a fleet — we're a family of dedicated drivers who support each other on every haul.
                </p>
              </div>
              <div className="border-l-4 border-[#27272a] pl-6">
                <p className="text-lg text-[#a1a1aa] leading-relaxed">
                  With round-the-clock fleet access, professional training programs, and a community that actually shows up — GTY is where drivers thrive.
                </p>
              </div>
            </div>

            {/* Stats — raw numbers, no icons */}
            <div className="grid grid-cols-2 border-2 border-[#27272a] divide-x-2 divide-y-2 divide-[#27272a]">
              <div className="p-7">
                <div className="text-5xl font-black mono-number text-white mb-1 leading-none">22</div>
                <div className="text-[#52525b] text-[10px] uppercase tracking-widest mt-2">Trucks in fleet</div>
              </div>
              <div className="p-7">
                <div className="text-5xl font-black mono-number text-white mb-1 leading-none">24/7</div>
                <div className="text-[#52525b] text-[10px] uppercase tracking-widest mt-2">Fleet access</div>
              </div>
              <div className="p-7">
                <div className="text-5xl font-black mono-number text-[#a855f7] mb-1 leading-none">FREE</div>
                <div className="text-[#52525b] text-[10px] uppercase tracking-widest mt-2">Maintenance</div>
              </div>
              <div className="p-7">
                <div className="text-5xl font-black mono-number text-white mb-1 leading-none">100%</div>
                <div className="text-[#52525b] text-[10px] uppercase tracking-widest mt-2">Community driven</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Benefits — numbered list ──────────────────────── */}
      <div className="border-t-2 border-[#141417]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-20">
          <span className="overline">What We Offer</span>
          <h2 className="text-4xl md:text-5xl font-black mt-4 mb-14 tracking-tighter uppercase">
            Employee Benefits
          </h2>

          <div className="border-t-2 border-[#1c1c1f]">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.num}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                className="flex items-baseline gap-8 py-6 border-b-2 border-[#1c1c1f] group"
                data-testid={`benefit-${i}`}
              >
                <span className="mono-number text-xs font-black text-[#3f3f46] w-7 flex-shrink-0 group-hover:text-[#7c3aed] transition-colors duration-150">
                  {b.num}
                </span>
                <div className="flex-1 md:grid md:grid-cols-[240px_1fr] md:gap-8 md:items-baseline">
                  <h3 className="font-black uppercase tracking-tight text-white text-base md:text-lg">
                    {b.label}
                  </h3>
                  <p className="text-[#71717a] leading-relaxed text-sm md:text-base mt-1 md:mt-0">
                    {b.detail}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA — pure typography, no card ───────────────── */}
      <div className="border-t-4 border-[#7c3aed]" data-testid="join-section">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div>
              <p className="text-[#7c3aed] text-[10px] font-black uppercase tracking-[0.35em] mb-5">
                Now taking applications
              </p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.92]">
                Ready<br />to Haul?
              </h2>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-3"
                data-testid="join-team-button"
              >
                Apply Now
                <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default About;
